import { createSharedComposable } from '@vueuse/core'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import dayjs from 'dayjs'
import { type BannerActionKey, type BannerItems } from '@klausapp/app-navigation/types'

import { session } from '@/composables/useSession'
import { hasAccountRole } from '@/utils/roleUtils'
import i18n from '@/i18n'
import { useEvent } from '@/composables/useEvent'
import analytics from '@/utils/analytics'

export default createSharedComposable(() => {
  const router = useRouter()

  // USER LIMIT BANNER
  const userLimitVisible = ref(false)
  useEvent('user-limit-reached', () => (userLimitVisible.value = true))

  const userLimitReachedBanner = computed((): BannerItems | null => {
    if (!userLimitVisible.value) return null

    return {
      id: 'user-limit-reached',
      title: i18n.t('modules.application.banners.user_limit.title'),
      type: 'warning' as BannerItems['type'],
      link: {
        label: i18n.t('modules.application.banners.user_limit.link_label'),
        type: 'link' as BannerActionKey,
      },
      dismissable: true,
    }
  })

  // TRIAL BANNER
  const trialRemainingCount = computed(() => {
    const today = new Date()
    const remaining = dayjs(session.account.trialEndAt).diff(today, 'day')
    return remaining > 0 ? remaining : 0
  })

  const trialRemainingText = computed(() => {
    if (trialRemainingCount.value === 0)
      return hasAccountRole('ADMIN')
        ? i18n.t('modules.application.banners.trial_end.template_today_zd_only')
        : i18n.t('modules.application.banners.trial_end.template_today')
    else if (trialRemainingCount.value === 1)
      return hasAccountRole('ADMIN')
        ? i18n.t('modules.application.banners.trial_end.template_1_day_zd_only', { n: trialRemainingCount.value })
        : i18n.t('modules.application.banners.trial_end.template_1_day', { n: trialRemainingCount.value })
    return hasAccountRole('ADMIN')
      ? i18n.t('modules.application.banners.trial_end.template_days_zd_only', { n: trialRemainingCount.value })
      : i18n.t('modules.application.banners.trial_end.template_days', { n: trialRemainingCount.value })
  })

  const trialEndingVisible = computed(() => {
    const hasActiveTrial = session.account.status === 'active' && session.account.trialEndAt
    return hasActiveTrial && trialRemainingCount.value < 8 && !hasZDTrial.value
  })

  const hideZDTrialBanner = ref(false)
  const hasZDTrial = computed(() => trialRemainingCount.value > 8 && trialRemainingCount.value < 15)
  const zdTrialVisible = computed(() => hasZDTrial.value && !hideZDTrialBanner.value)

  // IN TRIAL BANNER ITEM
  const accountInTrialBanner = computed((): BannerItems | null => {
    if (!zdTrialVisible.value) return null

    const bannerType = trialRemainingCount.value < 8 ? 'warning' : 'info'
    return {
      id: 'zd-trial-info',
      title: i18n.t('modules.application.banners.account_in_trial.title', trialRemainingCount.value, {
        named: { number: trialRemainingCount.value },
      }),
      type: bannerType as BannerItems['type'],
      dismissable: trialRemainingCount.value > 7,
      ...(hasAccountRole('ADMIN')
        ? {
            link: {
              label: i18n.t('modules.application.banners.account_in_trial.link_label'),
              href: '/admin',
            },
          }
        : {}),
    }
  })

  const globalBanners = computed(() =>
    [userLimitReachedBanner.value, accountInTrialBanner.value].filter((banner) => banner !== null),
  )

  const handleBannerClick = (bannerId: string, action: BannerActionKey) => {
    if (bannerId === 'user-limit-reached') {
      if (action === 'link') return router.push({ name: 'settings.subscription' })
      else {
        userLimitVisible.value = false
        analytics.closeMessageClicked()
      }
    }

    if (bannerId === 'zd-trial-info') {
      hideZDTrialBanner.value = true
      analytics.closeMessageClicked()
    }
  }

  return {
    trialRemainingCount,
    trialRemainingText,
    trialEndingVisible,
    userLimitVisible,
    zdTrialVisible,
    hideZDTrialBanner,
    globalBanners,
    onBannerClick: handleBannerClick,
  }
})
